import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Journal = () => {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/journal`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (!posts.length) return null;

  return (
    <section id="journal" data-testid="journal-section" aria-labelledby="journal-heading" className="parchment-noise relative overflow-hidden border-t border-parchborder py-24 sm:py-32">
      <div className="blueprint-grid-light absolute inset-0 opacity-40" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="mb-4 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.35em] text-ember sm:text-[11px]" data-testid="journal-sheet-label">
              <span className="inline-block h-px w-10 bg-ember/70" />
              Sheet VHE-06 — Publishing, Education & Transformative Media
            </p>
            <h2 id="journal-heading" className="font-serif text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Insights <em className="ember-text">Journal</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-ink2">
            Thought leadership, literature, and transformative media from the VHE publishing arm.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {posts.map((p, i) => (
            <Reveal key={p.id} delay={0.08 * i}>
              <article
                data-testid={`journal-card-${p.slug}`}
                className="trace-card group flex h-full cursor-pointer flex-col border border-parchborder bg-white/70 p-7 backdrop-blur-sm"
                onClick={() => setOpen(p)}
                onKeyDown={(e) => e.key === "Enter" && setOpen(p)}
                tabIndex={0}
                role="button"
                aria-label={`Read article: ${p.title}`}
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-ember">{p.tag}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-royal/60">
                    {new Date(p.timestamp).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold leading-snug text-royal">{p.title}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-ink2">{p.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-colors group-hover:text-ember">
                  Read Full Article <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy/80 p-4 backdrop-blur-sm"
            onClick={() => setOpen(null)}
            data-testid="journal-article-overlay"
          >
            <motion.article
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="parchment-noise relative max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-gold/40 p-8 shadow-2xl sm:p-12"
              onClick={(e) => e.stopPropagation()}
              aria-label={open.title}
            >
              <button
                type="button"
                onClick={() => setOpen(null)}
                data-testid="journal-article-close"
                aria-label="Close article"
                className="absolute right-4 top-4 rounded-full border border-ink/20 p-2 text-ink transition-colors hover:border-ember hover:text-ember"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">{open.tag}</p>
              <h3 className="mt-3 font-serif text-2xl font-bold leading-tight text-ink sm:text-3xl">{open.title}</h3>
              <div className="gold-rule my-6 max-w-xs" aria-hidden="true" />
              <div className="space-y-5 font-serif text-lg leading-relaxed text-ink2">
                {open.body.split(/\n{2,}/).map((block, i) => {
                  if (block.startsWith("```")) {
                    return (
                      <pre key={i} className="overflow-x-auto border border-royal/30 bg-navy p-4 font-mono text-[10px] leading-snug text-goldlight sm:text-xs">
                        {block.replace(/```/g, "").trim()}
                      </pre>
                    );
                  }
                  if (/^\d+\.\s+\S/.test(block)) {
                    return <h4 key={i} className="pt-4 font-serif text-xl font-bold not-italic text-royal sm:text-2xl">{block}</h4>;
                  }
                  if (block.startsWith("•")) {
                    return <p key={i} className="border-l-2 border-gold/60 pl-4 text-base sm:text-lg">{block.slice(1).trim()}</p>;
                  }
                  return <p key={i}>{block}</p>;
                })}
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Journal;
